"use client";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageSquare, Clock, PlayCircle, CheckCheck } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import type { UnifiedCall } from "@/types/unified-call";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





type FeedbackStatus = "pending" | "in_progress" | "resolved";

interface ExistingFeedback {
  id: string;
  feedback_text: string;
  status: FeedbackStatus;
  created_at: string;
}

interface CallFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  call: UnifiedCall | null;
  onFeedbackChanged?: () => void;
}

export function CallFeedbackDialog({
  open,
  onOpenChange,
  call,
  onFeedbackChanged,
}: CallFeedbackDialogProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()



  const tUi = useTranslations("Calls.CallsCallFeedbackDialog")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();
  const [feedbackText, setFeedbackText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState<ExistingFeedback | null>(null);

  // Get conversation_id for voice agent calls
  const conversationId = call?.voiceAgentData?.conversationId;
  const voiceAgentId = call?.voiceAgent?.id;

  // Load existing feedback when dialog opens
  useEffect(() => {
    if (open && conversationId) {
      loadExistingFeedback();
    } else {
      setFeedbackText("");
      setExistingFeedback(null);
    }
  }, [open, conversationId]);

  const loadExistingFeedback = async () => {
    if (!conversationId) return;

    try {
      setIsLoading(true);
      const res = await fetchWithAuth(
        `/api/ai/feedback?reference_type=voice_response&reference_id=${conversationId}&limit=1`
      );
      const data = await res.json();

      if (data.items?.length > 0) {
        const feedback = data.items[0];
        setExistingFeedback({
          id: feedback.id,
          feedback_text: feedback.feedback_text,
          status: feedback.status,
          created_at: feedback.created_at,
        });
        setFeedbackText(feedback.feedback_text || "");
      } else {
        setExistingFeedback(null);
        setFeedbackText("");
      }
    } catch (error) {
      console.error("Failed to load existing feedback:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!conversationId || !feedbackText.trim()) return;

    try {
      setIsSaving(true);

      if (existingFeedback) {
        // Update existing feedback
        const res = await fetchWithAuth(`/api/ai/feedback/${existingFeedback.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ feedback_text: feedbackText.trim() }),
        });

        if (!res.ok) throw new Error("Failed to update feedback");
        toast.success(tUi("feedbackUpdated"));
      } else {
        // Create new feedback
        const res = await fetchWithAuth("/api/ai/feedback", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reference_type: "voice_response",
            reference_id: conversationId,
            feedback_text: feedbackText.trim(),
            agent_id: voiceAgentId,
            agent_type: "voice_agent",
          }),
        });

        if (!res.ok) throw new Error("Failed to create feedback");
        toast.success(tUi("feedbackAdded"));
      }

      onFeedbackChanged?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save feedback:", error);
      toast.error(tUi("failedToSaveFeedback"));
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (status: FeedbackStatus) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            <span><span key={tUi("pending")}>{tUi("pending")}</span></span>
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <PlayCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("inProgress")}>{tUi("inProgress")}</span></span>
          </Badge>
        );
      case "resolved":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCheck className="w-3 h-3 mr-1" />
            <span><span key={tUi("resolved")}>{tUi("resolved")}</span></span>
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(uiFormattingLocale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!call) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-600" />
            <span key={existingFeedback ? tUi("editFeedback") : tUi("addFeedback")}>{existingFeedback ? tUi("editFeedback") : tUi("addFeedback")}</span>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Call info */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium"><span><span key={tUi("call")}>{tUi("call")}</span></span>{call.id}</div>
                  <div className="text-gray-500">
                    {call.voiceAgent?.name || tUi("voiceAgent")}
                  </div>
                </div>
                <div className="text-right text-gray-500">
                  <div>{call.callerNumber}</div>
                  <div>{new Date(call.createdAt).toLocaleDateString(uiFormattingLocale)}</div>
                </div>
              </div>
            </div>

            {/* Existing feedback status */}
            {existingFeedback && (
              <div className="flex items-center justify-between">
                {getStatusBadge(existingFeedback.status)}
                <span className="text-xs text-gray-500"><TranslationText>{tUi.rich("createdValue0", { value0: String(formatDate(existingFeedback.created_at)) })}</TranslationText></span>
              </div>
            )}

            {/* Feedback text */}
            <div>
              <label className="text-sm font-medium mb-2 block"><span key={tUi("feedback")}>{tUi("feedback")}</span></label>
              <Textarea
                placeholder={tUi("describeTheIssueOrImprovementNeeded")}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading || !feedbackText.trim()}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
            ) : existingFeedback ? (
              tUi("updateFeedback")
            ) : (
              tUi("addFeedback")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
